<template>
  <transition name="fade">
    <div
      v-if="overlayActive"
      class="side-menu-container fixed top-0 right-0 bottom-0 left-0 bg-black-full bg-opacity-25 z-sidebar-overlay"
      style="z-index:10000"
    />
  </transition>
  <div class="px-24 pt-24">
    <div class="text-heading-small mb-12">
      {{ $lang('checkout', 'paypal_form_intro') }}
    </div>
    <TextInput
      class="mb-12"
      :placeholder="$lang('checkout', 'paypal_form_placeholder_email')"
      :init-value="email"
      :has-error="formValidated && (!email || !emailOk)"
      autocomplete="email"
      @submit-input="(input) => (email = input)"
    />

    <div v-if="type === 'seller'">
      <div class="flex justify-start items-center mt-12 mb-12">
        <div class="mr-12">
          <input
            v-model="shopAsCompany"
            type="checkbox"
          >
        </div>
        <div>{{ $lang('checkout', 'form_shop_as_company') }}</div>
      </div>

      <div v-if="shopAsCompany">
        <TextInput
          class="mb-12"
          :placeholder="$lang('checkout', 'form_placeholder_company')"
          :init-value="company"
          :has-error="formValidated && !company"
          autocomplete="organization"
          @submit-input="(input) => (company = input)"
        />
        <TextInput
          class="mb-24"
          :placeholder="$lang('checkout', 'form_placeholder_vat')"
          :init-value="vat"
          :has-error="(formValidated && !checkVat(vat)) || centraVatError"
          @submit-input="(input) => (vat = input)"
        />
      </div>
    </div>

    <TextInput
      class="mb-12"
      :placeholder="$lang('checkout', 'paypal_form_placeholder_first_name')"
      :init-value="firstName"
      :has-error="formValidated && !firstName"
      autocomplete="given-name"
      @submit-input="(input) => (firstName = input)"
    />
    <TextInput
      class="mb-12"
      :placeholder="$lang('checkout', 'paypal_form_placeholder_last_name')"
      :init-value="lastName"
      :has-error="formValidated && !lastName"
      autocomplete="family-name"
      @submit-input="(input) => (lastName = input)"
    />
    <TextInput
      class="mb-12"
      :placeholder="$lang('checkout', 'paypal_form_placeholder_address')"
      :init-value="address1"
      :has-error="formValidated && !address1"
      autocomplete="address-line1"
      @submit-input="(input) => (address1 = input)"
    />
    <TextInput
      class="mb-12"
      :placeholder="$lang('checkout', 'paypal_form_placeholder_zip_code')"
      :init-value="zipCode"
      :has-error="formValidated && !zipCode"
      autocomplete="postal-code"
      @submit-input="(input) => (zipCode = input)"
    />
    <TextInput
      class="mb-12"
      :placeholder="$lang('checkout', 'paypal_form_placeholder_city')"
      :init-value="city"
      :has-error="formValidated && !city"
      autocomplete="address-level2"
      @submit-input="(input) => (city = input)"
    />
    <TextInput
      class="mb-12"
      :placeholder="$lang('checkout', 'form_placeholder_phone')"
      :init-value="phone"
      :has-error="formValidated && !phone"
      autocomplete="phone"
      @submit-input="(input) => (phone = input)"
    />

    <div v-if="type === 'paypal'" class="mt-24">
      <div class="text-heading-small mb-24">
        {{ $lang('checkout', 'paypal_form_headline_other_email') }}
      </div>
      <div>
        <div class="pb-20">
          <CheckboxComponent
            :checked="!useOtherEmail"
            :highlight="true"
            :is-radio="true"
            :text="$lang('checkout', 'paypal_form_other_email_false')"
            @on-check="useOtherEmail = false"
          />
        </div>
        <div class="pb-20">
          <CheckboxComponent
            :checked="useOtherEmail"
            :highlight="true"
            :is-radio="true"
            :text="$lang('checkout', 'paypal_form_other_email_true')"
            @on-check="useOtherEmail = true"
          />
        </div>
      </div>
      <TextInput
        v-if="useOtherEmail"
        class="mb-24"
        :placeholder="$lang('checkout', 'paypal_form_placeholder_other_email')"
        :init-value="otherEmail"
        :has-error="
          formValidated &&
            useOtherEmail &&
            (!otherEmail || !otherEmailOk)
        "
        @submit-input="(input) => (otherEmail = input)"
      />
    </div>
    <ButtonComponent
      class="mt-16"
      :loading="payPalLoading"
      :text="$lang('checkout', 'apply_address_btn')"
      is-block
      @click="startPayment"
    />
    <div v-if="paymentError" class="mt-12">
      {{ $lang('checkout', 'paypal_error') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Ref } from 'vue';
import TextInput from '~/components/forms/TextInput.vue';
import CheckboxComponent from '~/components/forms/CheckboxComponent.vue';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import { useConfigStore } from '~/store/config';
import useApiFetch from '~/composables/useApiFetch';
import useOrderComplete, { type PaymentResultResponse } from '~/composables/useOrderComplete';
import type { CentraPaymentResponse } from '~/constants/types/cart';
import { useCartStore } from '~/store/cart';
import OrderItem from '~/models/order';
import { useUiStore } from '~/store/ui';
import type { SellerClub, SellerPerson } from '~/constants/types/seller';
import { waitForTagManagers } from '~/composables/useTracking';

//Seller
const { sellerCookieOptions } = useCookieConfig();
const clubCookie = useCookie<SellerClub>('club', sellerCookieOptions) as Ref<SellerClub | null | undefined>;
const sellerCookie = useCookie<SellerPerson>('seller', sellerCookieOptions) as Ref<SellerPerson | null | undefined>;
const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions) as Ref<boolean>;

const cartStore = useCartStore();
const uiStore = useUiStore();

const props = withDefaults(defineProps<{
  type: 'paypal' | 'seller',
  newsletter?: boolean,
}>(), {
  newsletter: true,
});

const config = useConfigStore();
const route = useRoute();
const { centraPost, handleApiError, centraPut } = useApiFetch();
const runtimeConfig = useRuntimeConfig();
const { sendGtmReceipt } = useOrderComplete();

const payPalLoading = ref(false);
const overlayActive = ref(false);
const email = ref('');
const otherEmail = ref('');
const firstName = ref('');
const lastName = ref('');
const address1 = ref('');
const zipCode = ref('');
const city = ref('');
const phone = ref('');
const company = ref('');
const vat = ref('');
const shopAsCompany = ref(false);
const formValidated = ref(false);
const paymentError = ref(false);
const useOtherEmail = ref(false);
const centraVatError = ref(false);
const emailOk = ref(false);
const otherEmailOk = ref(false);
  
const checkEmail = async(str: string) => {
  if (str.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
    const strPart = str.split('@');
    return await $fetch<boolean>(`/api/validateEmail/?domain=${strPart[1]}`);
  } else {
    return false;
  }
  
};

watch(email, async() => {
  if (await checkEmail(email.value)) {
    emailOk.value = true;
  } else {
    emailOk.value = false;
  }
});

watch(otherEmail, async() => {
  if (await checkEmail(otherEmail.value)) {
    otherEmailOk.value = true;
  } else {
    otherEmailOk.value = false;
  }
});

const checkVat = (str: string) => {
  if (config.market.country == 'SE') {
    return str.match(/^(SE)(\d{10}01$)/i) ;
  } else if (config.market.country == 'FI') {
    return str.match (/^(FI)(\d{8}$)/i) ;
  } else if (config.market.country == 'NO') {
    return str.match (/^(NO)(\d{9}$)/i) ;
  } else {
    return false;
  }
};

watch(vat, () => {
  centraVatError.value = false;
});

const startPayment = async() => {
  document.body.classList.add('locked');
  payPalLoading.value = true;
  overlayActive.value = true;
  console.log('emailOK: ' +email.value + '/' + emailOk.value);
  if (isSeller.value && (!clubCookie.value || !sellerCookie.value)) {
    document.body.classList.remove('locked');
    overlayActive.value = false;
    payPalLoading.value = false;
    uiStore.sellerDialogSettings.sellerMissing = true;
    uiStore.sellerDialogSettings.forceSellerSelection = true;
    uiStore.showSellerDialog = true;
    return;
  }
  if (
    !email.value ||
    !emailOk.value ||
    !firstName.value ||
    !lastName.value ||
    !address1.value ||
    !zipCode.value ||
    !city.value ||
    (useOtherEmail.value && (!otherEmail.value || !otherEmailOk.value)) ||
    (!phone.value && props.type === 'seller') ||
    (!company.value && shopAsCompany.value) ||
    (!checkVat(vat.value) && shopAsCompany.value)
  ) {
    formValidated.value = true;
    payPalLoading.value = false;
    overlayActive.value = false;
    document.body.classList.remove('locked');
    return;
  }
  const thankYouPage = config.pageByTemplate('template-thank-you-page')?.route || '';

  const body = {
    address: {
      country: config.market.country,
      newsletter: props.newsletter,
      email: email.value,
      firstName: firstName.value,
      lastName: lastName.value,
      address1: address1.value,
      zipCode: zipCode.value,
      city: city.value,
    },
    shippingAddress: {
      country: config.market.country,
      email: useOtherEmail.value ? otherEmail.value : email.value,
      firstName: firstName.value,
      lastName: lastName.value,
      address1: address1.value,
      zipCode: zipCode.value,
      city: city.value,
    },
    paymentMethod: props.type,
    paymentReturnPage: `${runtimeConfig.public.frontendUrl}/${config.market.country.toLowerCase()}/payment-return`.replace('http://', 'https://'),
    paymentFailedPage: `${runtimeConfig.public.frontendUrl}${route.path}`.replace('http://', 'https://'),
    termsAndConditions: true,
  };

  if (props.type === 'seller') {
    body.address.phoneNumber = phone.value.replace(/\s+/g,'');
    body.shippingAddress.phoneNumber = phone.value.replace(/\s+/g,'');
  }

  // company vatNumber
  if (shopAsCompany.value) {
    body.address.company = company.value;
    body.shippingAddress.company = company.value;
    body.address.vatNumber = vat.value;
  }
  if (props.type === 'seller') {
    const shipres = await centraPut('/shipping-methods/seller-delivery');
  }

  const res = await centraPost<CentraPaymentResponse>('/payment', body);
  
  if (res) {
    if (props.type === 'paypal') {
      window.location.href = res.url;
    } else {
      const timestamp = Math.floor(Date.now() / 1000);
      const transactionReference = 'test1';

      const payload = '?selectionId=' + res.selection +
        '&amount=' + cartStore._cartData.selection.totals.grandTotalPriceAsNumber +
      '&currency=' + cartStore._cartData.selection.currency +
      '&timestamp=' + timestamp +
      '&transactionReference=' + transactionReference;

      const res2 = await $fetch('/api/sellerPayment' + payload);

      if (res2) {
        // if your remove transaction, it will break, i have no idea what it's for
        const paymentResultPayload = {
          'paymentMethodFields': {
            selection: res.selection,
            signature: res2.base64encodedSignature,
            currency: cartStore._cartData.selection.currency,
            amount: cartStore._cartData.selection.totals.grandTotalPriceAsNumber,
            timestamp,
            transactionReference,
            success: true,
            transaction: { lorem: 'ipsum' },
          },
        };

        const res3 = await centraPost<PaymentResultResponse>('/payment-result', paymentResultPayload);

        if (res3) {
          const order = new OrderItem(res3.order);
          
          // Wait for tag managers to be ready, then send tracking
          await waitForTagManagers();
          sendGtmReceipt(order);
          
          const thankYouPage = config.pageByTemplate('template-thank-you-page');
          // Additional timeout to ensure tracking completes before navigation
          setTimeout(()=> {
            navigateTo(thankYouPage.route);
          }, 1000);
        } else {
          handleApiError();
        }

      } else {
        handleApiError();
      }

    }
  } else {
    const err = handleApiError();
    const errMessage = JSON.stringify(err?.data?.errors) || '';
    if ( errMessage.includes("Incorrect VAT Number") ) {
      centraVatError.value = true;
      payPalLoading.value = false;
      overlayActive.value = false;
      document.body.classList.remove('locked');
    };
  }

};

onMounted(()=> {
  if (route.query?.checkoutError) {
    paymentError.value = true;
  }
});

</script>
